import { memo } from 'react';
import { css } from '@emotion/react';
import type {
  CreateTrainByPackageRequest,
  PackageVersion,
} from '@weave/schema-gen-ts/dist/schemas/integrations/sync-config-api/v1/message.pb';
import {
  Button,
  DropdownField,
  Modal,
  ModalControlModalProps,
  SpinningLoader,
  Text,
  TextField,
  useControlledField,
  useFormField,
} from '@frontend/design-system';
import { type DraftTrainRow, type DraftTrainRowOptions, useDraftTrainRows } from '../shared/hooks/use-draft-train-rows';

interface DraftTrainModalProps {
  modalProps: ModalControlModalProps;
  artifacts: PackageVersion[];
  totalArtifacts?: number;
  isLoadingArtifacts: boolean;
  isArtifactsError: boolean;
  hasMoreArtifacts: boolean;
  isLoadingMoreArtifacts: boolean;
  onLoadMoreArtifacts: () => void;
  isSaving?: boolean;
  onConfirm: (payload: CreateTrainByPackageRequest) => void;
}

export const DraftTrainModal = ({
  modalProps,
  artifacts,
  totalArtifacts,
  isLoadingArtifacts,
  isArtifactsError,
  hasMoreArtifacts,
  isLoadingMoreArtifacts,
  onLoadMoreArtifacts,
  isSaving,
  onConfirm,
}: DraftTrainModalProps) => {
  const { onClose } = modalProps;
  const trainNameField = useFormField({ type: 'text', value: '' }, [modalProps.show]);
  const { rows, getRowOptions, canAddMore, hasArtifacts, addRow, removeRow, updatePackage, updateVersion } =
    useDraftTrainRows(artifacts, modalProps.show);
  const isValid =
    trainNameField.value.trim() !== '' &&
    rows.length > 0 &&
    rows.every((row) => row.packageName !== '' && row.versionTag !== '');

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  const handleConfirm = () => {
    if (!isValid) return;
    onConfirm({
      trainName: trainNameField.value.trim(),
      packages: rows.map(({ packageName, versionTag }) => ({ packageName, versionTag })),
    });
  };

  return (
    <Modal
      {...modalProps}
      onClose={handleClose}
      minWidth={860}
      css={modalStyle}
      disableCloseOnEscape
      disableCloseOnOverlayClick
    >
      <Modal.Header onClose={handleClose}>Draft new train</Modal.Header>
      <Modal.Body css={bodyStyle}>
        <TextField {...trainNameField} name='train-name' label='Train name' placeholder='Train Name' />

        <div css={sectionHeaderStyle}>
          <Text weight='bold'>Artifacts</Text>
          <Text size='small' color='subdued'>
            One row per package version.
          </Text>
        </div>

        {isLoadingArtifacts ? (
          <div css={artifactsStatusStyle} role='status'>
            <SpinningLoader size='small' />
            <Text>Loading artifacts...</Text>
          </div>
        ) : isArtifactsError ? (
          <div css={emptyStateStyle} role='alert'>
            <Text color='error'>Unable to load artifacts. Please try again later.</Text>
          </div>
        ) : hasArtifacts ? (
          <>
            <div css={rowsWrapperStyle}>
              {rows.map((row) => {
                return (
                  <DraftTrainRowFields
                    key={row.id}
                    row={row}
                    options={getRowOptions(row)}
                    disabled={isSaving}
                    canRemove={rows.length > 1}
                    onPackageChange={updatePackage}
                    onVersionChange={updateVersion}
                    onRemove={removeRow}
                  />
                );
              })}
            </div>

            <Button iconName='plus' variant='tertiary' size='small' onClick={addRow} disabled={isSaving || !canAddMore}>
              Add package
            </Button>
            {hasMoreArtifacts && (
              <Button
                variant='tertiary'
                size='small'
                loading={isLoadingMoreArtifacts}
                disabled={isSaving}
                onClick={onLoadMoreArtifacts}
              >
                Load more artifacts
              </Button>
            )}
            {totalArtifacts !== undefined && (
              <Text color='primary' css={artifactCountStyle} size='medium'>
                Showing {artifacts.length.toLocaleString()} of {totalArtifacts.toLocaleString()} Artifacts
              </Text>
            )}
          </>
        ) : (
          <div css={emptyStateStyle} role='status'>
            <Text color='subdued'>No artifacts available to select. Try again later.</Text>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: 'Save draft',
          onClick: handleConfirm,
          disabled: !isValid || isSaving || !hasArtifacts,
          loading: isSaving,
        }}
        secondary={{
          label: 'Cancel',
          onClick: handleClose,
          disabled: isSaving,
        }}
      />
    </Modal>
  );
};

interface LabeledDropdownProps {
  id: string;
  name: string;
  label: string;
  placeholder: string;
  value: string;
  disabled?: boolean;
  options: string[];
  onChange: (id: string, value: string) => void;
}

const LabeledDropdown = ({
  id,
  name,
  label,
  placeholder,
  value,
  disabled,
  options,
  onChange,
}: LabeledDropdownProps) => {
  const dropdownProps = useControlledField({
    type: 'dropdown',
    value,
    onChange: (nextValue) => onChange(id, nextValue),
  });

  return (
    <DropdownField {...dropdownProps} name={name} label={label} placeholder={placeholder} disabled={disabled}>
      {options.map((opt) => (
        <DropdownField.Option key={opt} value={opt}>
          {opt}
        </DropdownField.Option>
      ))}
    </DropdownField>
  );
};

interface DraftTrainRowFieldsProps {
  row: DraftTrainRow;
  options: DraftTrainRowOptions;
  disabled?: boolean;
  canRemove: boolean;
  onPackageChange: (id: string, packageName: string) => void;
  onVersionChange: (id: string, versionTag: string) => void;
  onRemove: (id: string) => void;
}

const DraftTrainRowFields = memo(function DraftTrainRowFields({
  row,
  options,
  disabled,
  canRemove,
  onPackageChange,
  onVersionChange,
  onRemove,
}: DraftTrainRowFieldsProps) {
  const hasVersions = options.versions.length > 0;

  return (
    <div css={rowStyle}>
      <div css={cellStyle}>
        <LabeledDropdown
          id={row.id}
          name={`package-${row.id}`}
          label='Package'
          placeholder='Select package'
          value={row.packageName ?? ''}
          disabled={disabled}
          options={options.packages}
          onChange={onPackageChange}
        />
      </div>
      <div css={cellStyle}>
        <LabeledDropdown
          id={row.id}
          name={`version-${row.id}`}
          label='Version'
          placeholder={hasVersions ? 'Select version' : 'Select package first'}
          value={row.versionTag ?? ''}
          disabled={disabled || !hasVersions}
          options={options.versions}
          onChange={onVersionChange}
        />
      </div>
      <div css={removeCellStyle}>
        <RemoveRowButton id={row.id} disabled={disabled || !canRemove} onRemove={onRemove} />
      </div>
    </div>
  );
});

interface RemoveRowButtonProps {
  id: string;
  disabled?: boolean;
  onRemove: (id: string) => void;
}

const RemoveRowButton = ({ id, disabled, onRemove }: RemoveRowButtonProps) => {
  return (
    <Button
      iconName='trash'
      hoverLabel='Remove row'
      variant='tertiary'
      size='small'
      onClick={() => onRemove(id)}
      disabled={disabled}
    />
  );
};

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-md);
  padding-top: var(--fabric-space-padding-md);
`;

const modalStyle = css`
  max-height: 600px;
`;

const sectionHeaderStyle = css`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const rowsWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xs);
`;

const rowStyle = css`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: var(--fabric-space-gap-sm);
  align-items: end;
`;

const cellStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xs);
`;

const removeCellStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
`;

const emptyStateStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--fabric-space-padding-lg) var(--fabric-space-padding-md);
  border: var(--fabric-border-width-thin) dashed var(--fabric-color-neutral-30);
  border-radius: var(--fabric-radius-md);
  background-color: var(--fabric-color-neutral-5);
`;

const artifactsStatusStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--fabric-space-gap-xs);
  min-height: 120px;
`;

const artifactCountStyle = css`
  text-align: center;
`;
